import * as React from 'react';
import { Select, DatePicker } from 'antd';
import moment from 'moment';
import withStyles from 'react-jss';
import { ThemeType } from 'src/theme/jss-types';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from 'src/utils/utils';

interface DateRangeType {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

interface DateCyclePickerProps {
  cycleType?: 'monthly' | 'weekly' | null;
  cycleStart?: number;
  onChange: (dateRange: DateRangeType | null) => void;
  minWidth?: string;
  value?: DateRangeType | null;
  classes: any;
}

const styles = (theme: ThemeType) => ({
  datePicker: {
    width: '100%',
    padding: 0,
    '&.ant-select .ant-select-selector': {
      backgroundColor: '#fff !important',
      border: '1px solid #999999 !important',
      borderRadius: '0px 4px 4px 0px !important',
    },
  },
  dropDown: {
    padding: '8px 8px',
    borderBottom: '1px solid #f0f0f0',
  },
  rangePicker: {
    position: 'relative',
    flexGrow: 1,
    '& .ant-picker': {
      height: '32px',
      padding: '4px 11px',
    },
  },
});

/*
  Returns the default date range based on the cycle type and start day.
  For monthly or weekly cycles, the default date range is the current billing period.
  If no cycle type is provided, it defaults to the last 30 days.
*/
export const getDefaultDateRange = (
  cycleType?: 'monthly' | 'weekly',
  cycleStart?: number,
): DateRangeType => {
  const today = moment();

  if (cycleType === 'monthly') {
    const startDay = Number(cycleStart);
    const currentMonth = today.clone().date(1);

    if (today.date() < startDay) {
      const prevMonth = currentMonth.clone().subtract(1, 'month');
      const startDate = prevMonth.clone().date(Math.min(startDay, prevMonth.daysInMonth()));
      const nextMonth = startDate.clone().add(1, 'month');
      const endDate = nextMonth
        .clone()
        .date(Math.min(startDay, nextMonth.daysInMonth()))
        .subtract(1, 'day');

      return { startDate, endDate };
    }

    const startDate = currentMonth.clone().date(Math.min(startDay, currentMonth.daysInMonth()));
    const nextMonth = startDate.clone().add(1, 'month');
    const endDate = nextMonth
      .clone()
      .date(Math.min(startDay, nextMonth.daysInMonth()))
      .subtract(1, 'day');

    return { startDate, endDate };
  }

  if (cycleType === 'weekly') {
    const startDay = Number(cycleStart) % 7;
    const startDate = today.clone();

    while (startDate.day() !== startDay) {
      startDate.subtract(1, 'day');
    }

    const endDate = startDate.clone().add(6, 'days');

    return { startDate, endDate };
  }

  return {
    startDate: today.clone().subtract(30, 'days').startOf('day'),
    endDate: today.clone().endOf('day'),
  };
};

const DateCyclePicker: React.FC<DateCyclePickerProps> = ({
  cycleType,
  cycleStart,
  onChange,
  minWidth = '200px',
  value,
  classes,
}) => {
  const [cycleState, setCycleState] = React.useState({
    month: moment(),
    cycles: [],
    selectedCycleIndex: -1,
    dropdownOpen: false,
  });
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    const cycles = [];

    if (cycleType === 'monthly' && cycleState.month) {
      /*
        Cycle will be from min(startDay, last day of month) to min(startDay, last day of next month)
        Example: if startDay is 29, then:
          29 Jan - 27 Feb, 28 Feb - 28 Mar and so on.. for non leap year
          29 Jan - 28 Feb, 29 Feb - 28 Mar and so on.. for leap year
      */
      const startDay = Number(cycleStart);
      const currentYear = cycleState.month.year();
      const startOfYear = moment().year(currentYear).month(0).date(1);
      const endOfYear = moment().year(currentYear).month(11).endOf('month');
      let baseDate = moment()
        .year(currentYear - 1)
        .month(11)
        .date(1);

      while (baseDate.isBefore(endOfYear)) {
        const startDate = baseDate
          .clone()
          .date(Math.min(startDay, baseDate.daysInMonth()));
        const nextMonth = startDate.clone().add(1, 'month');
        const endDate = nextMonth
          .clone()
          .date(Math.min(startDay, nextMonth.daysInMonth()))
          .subtract(1, 'day');

        if (startDate.isAfter(moment())) {
          break;
        }

        if (endDate.isSameOrAfter(startOfYear, 'day')) {
          cycles.push({
            startDate,
            endDate,
            displayText: `${formatDateTime(startDate, 'D MMM YYYY')} - ${formatDateTime(endDate, 'D MMM YYYY')}`,
          });
        }
        baseDate = baseDate.clone().add(1, 'month');
      }
    } else if (cycleType === 'weekly' && cycleState.month) {
      /*
        Cycles will be from startDay of the week (0-Sunday) to startDay-1 of next week
        Example: if startDay is 3, then:
          Last Wednesday of previous month - First Tuesday of current month,
          FIrst Wednesday of current month - Second Tuesday of current month,
          ...
          Last Wednesday of current month - First Tuesday of next month
      */
      const startDay = Number(cycleStart) % 7;
      const startDate = cycleState.month.clone().startOf('month').subtract(6, 'days');
      const endOfMonth = cycleState.month.clone().endOf('month');

      let currentCycleStart = startDate.clone();

      while (currentCycleStart.isBefore(endOfMonth)) {
        const nextAnchorDay = currentCycleStart.clone();

        while (nextAnchorDay.day() !== startDay) {
          nextAnchorDay.add(1, 'day');
        }

        const cycleEnd = nextAnchorDay.clone().add(6, 'days');

        if (
          !cycleEnd.isBefore(cycleState.month.clone().startOf('month')) &&
          !currentCycleStart.isAfter(cycleState.month.clone().endOf('month')) &&
          !nextAnchorDay.isAfter(cycleState.month.clone().endOf('month')) &&
          !nextAnchorDay.isAfter(moment())
        ) {
          cycles.push({
            startDate: nextAnchorDay,
            endDate: cycleEnd,
            displayText: `${formatDateTime(nextAnchorDay, 'D MMM YYYY')} - ${formatDateTime(cycleEnd, 'D MMM YYYY')}`,
          });
        }

        currentCycleStart = nextAnchorDay.clone().add(1, 'day');
      }
    }

    if (value?.startDate && value.endDate) {
      const matchingIndex = cycles.findIndex(
        (cycle) =>
          cycle.startDate.isSame(value.startDate, 'day') &&
          cycle.endDate.isSame(value.endDate, 'day'),
      );

      if (matchingIndex !== -1) {
        setCycleState((prev) => ({ ...prev, cycles, selectedCycleIndex: matchingIndex }));
      } else {
        setCycleState((prev) => ({ ...prev, cycles, selectedCycleIndex: -1 }));
      }
    } else if (cycles.length > 0) {
      // Auto-select the latest (most recent) cycle
      const latestIndex = cycles.length - 1;
      setCycleState((prev) => ({ ...prev, cycles, selectedCycleIndex: latestIndex }));
      // Trigger onChange with the latest cycle
      const latestCycle = cycles[latestIndex];
      if (latestCycle) {
        onChange({
          startDate: latestCycle.startDate,
          endDate: latestCycle.endDate,
        });
      }
    } else {
      setCycleState((prev) => ({ ...prev, cycles, selectedCycleIndex: -1 }));
    }
  }, [cycleState.month, cycleType, cycleStart, value]);

  if (cycleType === 'monthly') {
    return (
      <div style={{ position: 'relative', flexGrow: 1, minWidth }}>
        <Select
          className={classes.datePicker}
          placeholder={t('select_cycle')}
          value={
            cycleState.selectedCycleIndex !== -1 ? `${cycleState.selectedCycleIndex}` : undefined
          }
          onChange={(selectedValue) => {
            const index = parseInt(selectedValue, 10);
            setCycleState((prev) => ({ ...prev, selectedCycleIndex: index, dropdownOpen: false }));
            if (cycleState.cycles[index]) {
              const selectedCycle = cycleState.cycles[index];
              onChange({
                startDate: selectedCycle.startDate,
                endDate: selectedCycle.endDate,
              });
            }
          }}
          open={cycleState.dropdownOpen}
          onDropdownVisibleChange={(visible) => {
            if (visible) {
              setCycleState((prev) => ({ ...prev, dropdownOpen: true }));
            } else if (!calendarOpen) {
              setCycleState((prev) => ({ ...prev, dropdownOpen: false }));
            }
          }}
          dropdownRender={(menu) => (
            <div onClick={(e) => e.stopPropagation()}>
              <div className={classes.dropDown}>
                <DatePicker
                  picker="year"
                  bordered={false}
                  value={cycleState.month}
                  disabledDate={(current) => current?.isAfter(moment(), 'month')}
                  format="YYYY"
                  className={classes.datePicker}
                  onOpenChange={(open) => setCalendarOpen(open)}
                  onChange={(date) => {
                    if (date) {
                      setCycleState((prev) => ({ ...prev, month: date }));
                    }
                  }}
                />
              </div>
              {menu}
            </div>
          )}
          options={cycleState.cycles.map((c, i) => ({
            label: c.displayText,
            value: `${i}`,
          }))}
          dropdownStyle={{ pointerEvents: calendarOpen ? 'none' : 'auto' }}
        />
      </div>
    );
  }

  if (cycleType === 'weekly') {
    return (
      <div style={{ position: 'relative', flexGrow: 1, minWidth }}>
        <Select
          className={classes.datePicker}
          placeholder={t('select_cycle')}
          value={
            cycleState.selectedCycleIndex !== -1 ? `${cycleState.selectedCycleIndex}` : undefined
          }
          onChange={(selectedValue) => {
            const index = parseInt(selectedValue, 10);
            setCycleState((prev) => ({ ...prev, selectedCycleIndex: index, dropdownOpen: false }));
            if (cycleState.cycles[index]) {
              const selectedCycle = cycleState.cycles[index];
              onChange({
                startDate: selectedCycle.startDate,
                endDate: selectedCycle.endDate,
              });
            }
          }}
          open={cycleState.dropdownOpen}
          onDropdownVisibleChange={(visible) => {
            if (visible) {
              setCycleState((prev) => ({ ...prev, dropdownOpen: true }));
            } else if (!calendarOpen) {
              setCycleState((prev) => ({ ...prev, dropdownOpen: false }));
            }
          }}
          dropdownRender={(menu) => (
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className={classes.dropDown}>
                <DatePicker
                  picker="month"
                  bordered={false}
                  value={cycleState.month}
                  onChange={(date) => {
                    if (date) {
                      setCycleState((prev) => ({ ...prev, month: date }));
                    }
                  }}
                  disabledDate={(current) => current?.isAfter(moment(), 'year')}
                  format="MMMM YYYY"
                  className={classes.datePicker}
                  onOpenChange={(open) => {
                    setCalendarOpen(open);
                    if (open) {
                      setCycleState((prev) => ({ ...prev, dropdownOpen: true }));
                    }
                  }}
                />
              </div>
              {menu}
            </div>
          )}
          options={cycleState.cycles.map((c, i) => ({
            label: c.displayText,
            value: `${i}`,
          }))}
          dropdownStyle={{ pointerEvents: calendarOpen ? 'none' : 'auto' }}
        />
      </div>
    );
  }

  return (
    <div className={classes.rangePicker}>
      <DatePicker.RangePicker
        className={classes.datePicker}
        disabledDate={(current) => current?.isAfter(moment(), 'day')}
        onChange={(dates) => {
          if (dates?.[0] && dates[1]) {
            onChange({
              startDate: dates[0],
              endDate: dates[1],
            });
          } else {
            onChange(null);
          }
        }}
        value={value?.startDate && value?.endDate ? [value.startDate, value.endDate] : undefined}
        ranges={{
          Today: [moment(), moment()],
          'Last 7 days': [moment().subtract(6, 'day'), moment()],
          'Last 30 days': [moment().subtract(29, 'day'), moment()],
          'This Month': [moment().startOf('month'), moment()],
        }}
        placeholder={['Start date', 'End date']}
        format="DD MMM YYYY"
      />
    </div>
  );
};

export default withStyles(styles, { injectTheme: true })(DateCyclePicker);
